import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Server, Database, Cloud, ExternalLink, Activity, GitBranch, Network, 
  Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, 
  ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, X, Eye, Clock, 
  User, Users, Calendar, Shield, CheckCircle, XCircle, AlertCircle,
  Filter, LayoutGrid, List, RefreshCw, ChevronDown, ChevronUp,
  Zap, FileText, Timer, Tag
} from 'lucide-react';
import InfraChangeDetail from './InfraChangeDetail';
import api from '../utils/api';

const Infrastructure = () => {
  // State
  const [changes, setChanges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChange, setSelectedChange] = useState(null);
  const [showAll, setShowAll] = useState(false);
  
  // View & Layout
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [expandedCards, setExpandedCards] = useState({});
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    approval: [],
    risk: [],
    changeType: [],
    assignmentGroup: '',
    dateRange: 'all'
  });
  
  // Pagination & Sort
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [sortConfig, setSortConfig] = useState({ key: 'start_date', direction: 'desc' });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const changesData = await api.request('/infra-changes');
      const list = changesData.changes || [];
      setChanges(list);

      // Derive the summary counts from the records we already have.
      // There is no /infra-changes/stats endpoint - it was never implemented,
      // so the previous call always failed and these cards never rendered.
      const now = Date.now();
      const in24h = now + 24 * 60 * 60 * 1000;
      const parseDate = (s) => {
        const t = Date.parse((s || '').replace(' ', 'T') + 'Z');
        return Number.isNaN(t) ? null : t;
      };
      setStats({
        total_changes: list.length,
        approved: list.filter((c) => c.approval_state === 'Approved').length,
        pending: list.filter((c) => c.approval_state && c.approval_state !== 'Approved').length,
        in_implementation: list.filter((c) => c.change_state === 'Implementation Authorized').length,
        emergency: list.filter((c) => c.change_type === 'Emergency').length,
        upcoming_24h: list.filter((c) => {
          const t = parseDate(c.start_date);
          return t !== null && t >= now && t <= in24h;
        }).length,
      });
    } catch (err) {
      console.error('[Infrastructure] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper functions
  const getStatusColor = (state) => {
    const stateColors = {
      'Approved': 'bg-green-500/20 text-green-400 border-green-500/40',
      'Implementation Authorized': 'bg-blue-500/20 text-blue-400 border-blue-500/40',
      'Pending': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      'Not Yet Requested': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      'Emergency': 'bg-red-500/20 text-red-400 border-red-500/40',
      'Closed': 'bg-gray-500/20 text-text-secondary border-gray-500/40',
      'Closed Complete': 'bg-gray-500/20 text-text-secondary border-gray-500/40',
      'Planning': 'bg-purple-500/20 text-purple-400 border-purple-500/40',
      'Scheduled': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
      'In Progress': 'bg-orange-500/20 text-orange-400 border-orange-500/40',
      'Implement': 'bg-orange-500/20 text-orange-400 border-orange-500/40'
    };
    return stateColors[state] || 'bg-gray-500/20 text-text-secondary border-gray-500/40';
  };

  const getRiskIcon = (risk) => {
    if (!risk) return <Shield size={14} className="text-text-muted" />;
    const riskLower = risk.toLowerCase();
    if (riskLower.includes('high')) return <Shield size={14} className="text-red-500" />;
    if (riskLower.includes('medium') || riskLower.includes('moderate')) return <Shield size={14} className="text-yellow-500" />;
    return <Shield size={14} className="text-green-500" />;
  };

  const getRiskColor = (risk) => {
    if (!risk) return 'text-text-secondary';
    const riskLower = risk.toLowerCase();
    if (riskLower.includes('high')) return 'text-red-400';
    if (riskLower.includes('medium') || riskLower.includes('moderate')) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getChangeTypeColor = (type) => {
    if (type === 'Emergency') return 'bg-red-500/20 text-red-400 border-red-500/40';
    if (type === 'Standard') return 'bg-green-500/20 text-green-400 border-green-500/40';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/40';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return 'N/A'; }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = date - now;
      const hours = Math.abs(diff) / 36e5;
      
      if (diff > 0 && hours < 24) return `Starts in ${Math.round(hours)}h`;
      if (diff > 0 && hours < 168) return `Starts in ${Math.round(hours/24)}d`;
      if (diff < 0 && hours < 24) return `${Math.round(hours)}h ago`;
      if (diff < 0 && hours < 168) return `${Math.round(hours/24)}d ago`;
      return '';
    } catch { return ''; }
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    try {
      const diff = new Date(end) - new Date(start);
      const hours = Math.floor(diff / 36e5);
      const mins = Math.floor((diff % 36e5) / 60000);
      if (hours > 24) return `${Math.floor(hours/24)}d ${hours%24}h`;
      return `${hours}h ${mins}m`;
    } catch { return null; }
  };

  const getCIMatchDisplay = (change) => {
    const total = change.ci_total_count || 0;
    const matched = change.ci_matched_count || 0;
    const needsReview = change.ci_needs_review_count || 0;
    
    if (total === 0) return { text: 'No CIs — needs review', color: 'text-amber-400', percent: 0 };
    
    const percent = Math.round((matched / total) * 100);
    let color = 'text-green-400';
    if (percent < 70) color = 'text-yellow-400';
    if (percent < 50) color = 'text-red-400';
    
    return { 
      text: `${matched}/${total} matched`, 
      color, 
      percent,
      needsReview 
    };
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = changes.filter(item => {
      // Time filter - last 12 hours to next 12 hours unless showAll is true
      if (!showAll) {
        const now = new Date();
        const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        const twelveHoursFromNow = new Date(now.getTime() + 12 * 60 * 60 * 1000);
        
        if (item.start_date) {
          const startDate = new Date(item.start_date);
          if (startDate < twelveHoursAgo || startDate > twelveHoursFromNow) {
            return false;
          }
        }
      }
      
      // Search
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          (item.change_number || '').toLowerCase().includes(search) ||
          (item.short_description || '').toLowerCase().includes(search) ||
          (item.assigned_to || '').toLowerCase().includes(search) ||
          (item.assignment_group || '').toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(item.change_state)) return false;
      
      // Approval filter
      if (filters.approval.length > 0 && !filters.approval.includes(item.approval_state)) return false;
      
      // Risk filter
      if (filters.risk.length > 0) {
        const riskMatch = filters.risk.some(r => (item.risk || '').toLowerCase().includes(r.toLowerCase()));
        if (!riskMatch) return false;
      }
      
      // Change type filter
      if (filters.changeType.length > 0 && !filters.changeType.includes(item.change_type)) return false;
      
      // Assignment group filter
      if (filters.assignmentGroup && item.assignment_group !== filters.assignmentGroup) return false;
      
      return true;
    });

    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'start_date' || sortConfig.key === 'end_date') {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [changes, searchTerm, filters, sortConfig, showAll]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleCardExpand = (changeNumber) => {
    setExpandedCards(prev => ({
      ...prev,
      [changeNumber]: !prev[changeNumber]
    }));
  };

  // Show detail view if selected
  if (selectedChange) {
    return (
      <InfraChangeDetail 
        changeNumber={selectedChange} 
        onBack={() => setSelectedChange(null)} 
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Quick Stats Panel */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard label="Total Changes" value={stats.total_changes} icon={FileText} color="purple" />
          <StatCard label="Approved" value={stats.approved} icon={CheckCircle} color="green" />
          <StatCard label="Pending" value={stats.pending} icon={Clock} color="yellow" />
          <StatCard label="In Implementation" value={stats.in_implementation} icon={Zap} color="blue" />
          <StatCard label="Emergency" value={stats.emergency} icon={AlertTriangle} color="red" />
          <StatCard label="Next 24h" value={stats.upcoming_24h} icon={Timer} color="orange" />
        </div>
      )}

      {/* Header with Search and Controls */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-blue-400">
            Infrastructure Changes
          </h1>
          <button
            onClick={fetchData}
            className="p-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 transition-all"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowAll(!showAll)}
            className={`px-3 py-2 rounded border transition-all ${
              showAll 
                ? 'bg-blue-500/20 border-blue-500/40 text-blue-400' 
                : 'bg-dark-border/40 border-border/60 text-text-secondary hover:text-blue-400'
            }`}
          >
            {showAll ? 'Show 24h Window' : 'Display All'}
          </button>
        </div>
        
        <div className="flex items-center gap-3 w-full lg:w-auto">
          {/* Search */}
          <div className="relative flex-1 lg:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search changes, CIs, assignees..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded text-sm text-text-primary placeholder-gray-500 focus:outline-none focus:border-blue-500/60"
            />
          </div>
          
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded border transition-all ${
              showFilters 
                ? 'bg-blue-500/20 border-blue-500/40 text-blue-400' 
                : 'bg-dark-border/40 border-border/60 text-text-secondary hover:text-blue-400'
            }`}
          >
            <Filter size={16} />
          </button>
          
          {/* View Toggle */}
          <div className="flex rounded border border-border/60 overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 transition-all ${viewMode === 'cards' ? 'bg-blue-500/20 text-blue-400' : 'bg-dark-border/40 text-text-secondary'}`}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 transition-all ${viewMode === 'table' ? 'bg-blue-500/20 text-blue-400' : 'bg-dark-border/40 text-text-secondary'}`}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <FilterPanel filters={filters} setFilters={setFilters} changes={changes} />
      )}

      {/* Loading / Error States */}
      {loading && (
        <div className="bg-surface rounded-lg border border-border p-12 text-center">
          <RefreshCw size={32} className="animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-text-secondary">Loading infrastructure changes...</p>
        </div>
      )}

      {error && (
        <div className="bg-surface rounded-lg border border-red-500/40 p-8 text-center">
          <AlertTriangle size={32} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-2">Error loading data</p>
          <p className="text-sm text-text-muted mb-4">{error}</p>
          <button onClick={fetchData} className="px-4 py-2 bg-blue-500/20 border border-blue-500/40 text-blue-400 rounded hover:bg-blue-500/30">
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Card View */}
          {viewMode === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedData.map((change) => (
                <ChangeCard 
                  key={change.change_number}
                  change={change}
                  expanded={expandedCards[change.change_number]}
                  onToggleExpand={() => toggleCardExpand(change.change_number)}
                  onViewDetails={() => setSelectedChange(change.change_number)}
                  getStatusColor={getStatusColor}
                  getRiskIcon={getRiskIcon}
                  getRiskColor={getRiskColor}
                  getChangeTypeColor={getChangeTypeColor}
                  formatDate={formatDate}
                  formatRelativeTime={formatRelativeTime}
                  calculateDuration={calculateDuration}
                  getCIMatchDisplay={getCIMatchDisplay}
                />
              ))}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <TableView 
              data={paginatedData}
              sortConfig={sortConfig}
              onSort={(key) => setSortConfig(prev => ({
                key,
                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
              }))}
              onViewDetails={setSelectedChange}
              getStatusColor={getStatusColor}
              getRiskColor={getRiskColor}
              formatDate={formatDate}
              getCIMatchDisplay={getCIMatchDisplay}
            />
          )}

          {/* Empty State */}
          {paginatedData.length === 0 && (
            <div className="bg-surface rounded-lg border border-border p-12 text-center">
              <FileText size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-text-secondary">No infrastructure changes found</p>
              {searchTerm && <p className="text-sm text-text-muted mt-2">Try adjusting your search or filters</p>}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredAndSortedData.length}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
            />
          )}
        </>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    purple: 'border-blue-500/40 text-blue-400',
    green: 'border-green-500/40 text-green-400',
    yellow: 'border-yellow-500/40 text-yellow-400',
    blue: 'border-blue-500/40 text-blue-400',
    red: 'border-red-500/40 text-red-400',
    orange: 'border-orange-500/40 text-orange-400'
  };
  
  return (
    <div className={`bg-surface rounded-lg border ${colors[color]} p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-text-muted uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold ${colors[color].split(' ')[1]}`}>{value || 0}</p>
        </div>
        <Icon size={24} className={colors[color].split(' ')[1]} style={{ opacity: 0.5 }} />
      </div>
    </div>
  );
};


// Filter Panel Component
const FilterPanel = ({ filters, setFilters, changes }) => {
  // Get unique values for filters
  const uniqueStates = [...new Set(changes.map(c => c.change_state).filter(Boolean))];
  const uniqueApprovals = [...new Set(changes.map(c => c.approval_state).filter(Boolean))];
  const uniqueTypes = [...new Set(changes.map(c => c.change_type).filter(Boolean))];
  const uniqueGroups = [...new Set(changes.map(c => c.assignment_group).filter(Boolean))];

  const toggleFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value) 
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: [],
      approval: [],
      risk: [],
      changeType: [],
      assignmentGroup: '',
      dateRange: 'all'
    });
  };

  return (
    <div className="bg-surface rounded-lg border border-border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Filters</h3>
        <button onClick={clearFilters} className="text-xs text-text-secondary hover:text-blue-400">
          Clear All
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Change State</label>
          <div className="flex flex-wrap gap-1">
            {uniqueStates.slice(0, 6).map(state => (
              <button
                key={state}
                onClick={() => toggleFilter('status', state)}
                className={`px-2 py-1 text-xs rounded border transition-all ${
                  filters.status.includes(state)
                    ? 'bg-blue-500/20 border-blue-500/40 text-blue-400'
                    : 'bg-dark-border/40 border-border/60 text-text-secondary hover:border-blue-500/40'
                }`}
              >
                {state}
              </button>
            ))}
          </div>
        </div>

        {/* Approval Filter */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Approval</label>
          <div className="flex flex-wrap gap-1">
            {uniqueApprovals.map(approval => (
              <button
                key={approval}
                onClick={() => toggleFilter('approval', approval)}
                className={`px-2 py-1 text-xs rounded border transition-all ${
                  filters.approval.includes(approval)
                    ? 'bg-green-500/20 border-green-500/40 text-green-400'
                    : 'bg-dark-border/40 border-border/60 text-text-secondary hover:border-green-500/40'
                }`}
              >
                {approval}
              </button>
            ))}
          </div>
        </div>

        {/* Risk Filter */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Risk Level</label>
          <div className="flex flex-wrap gap-1">
            {['Low', 'Medium', 'High'].map(risk => (
              <button
                key={risk}
                onClick={() => toggleFilter('risk', risk)}
                className={`px-2 py-1 text-xs rounded border transition-all ${
                  filters.risk.includes(risk)
                    ? risk === 'High' ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : risk === 'Medium' ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400'
                      : 'bg-green-500/20 border-green-500/40 text-green-400'
                    : 'bg-dark-border/40 border-border/60 text-text-secondary'
                }`}
              >
                {risk}
              </button>
            ))}
          </div>
        </div>

        {/* Change Type Filter */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Change Type</label>
          <div className="flex flex-wrap gap-1">
            {uniqueTypes.map(type => (
              <button
                key={type}
                onClick={() => toggleFilter('changeType', type)}
                className={`px-2 py-1 text-xs rounded border transition-all ${
                  filters.changeType.includes(type)
                    ? type === 'Emergency' ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : 'bg-blue-500/20 border-blue-500/40 text-blue-400'
                    : 'bg-dark-border/40 border-border/60 text-text-secondary'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Assignment Group Dropdown */}
      <div>
        <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Assignment Group</label>
        <select
          value={filters.assignmentGroup}
          onChange={(e) => setFilters(prev => ({ ...prev, assignmentGroup: e.target.value }))}
          className="w-full md:w-64 px-3 py-2 bg-dark-border/40 border border-border/60 rounded text-sm text-text-primary focus:outline-none focus:border-blue-500/60"
        >
          <option value="">All Groups</option>
          {uniqueGroups.map(group => (
            <option key={group} value={group}>{group}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Change Card Component
const ChangeCard = ({ 
  change, expanded, onToggleExpand, onViewDetails,
  getStatusColor, getRiskIcon, getRiskColor, getChangeTypeColor,
  formatDate, formatRelativeTime, calculateDuration, getCIMatchDisplay
}) => {
  const ciMatch = getCIMatchDisplay(change);
  const duration = calculateDuration(change.start_date, change.end_date);
  const relativeTime = formatRelativeTime(change.start_date);
  const isUpcoming = relativeTime.includes('Starts in');
  const isUrgent = isUpcoming && relativeTime.includes('h');

  return (
    <div 
      className={`bg-surface rounded-lg border transition-all hover:border-blue-500/40 ${
        change.risk === 'High' ? 'border-red-500/50 bg-red-950/10' :
        isUrgent ? 'border-blue-500/60' : 'border-border'
      }`}
      
    >
      {/* Top Row - Critical Info */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={onViewDetails}
                className="text-lg font-bold text-blue-400 hover:text-purple-300 transition-colors"
              >
                {change.change_number}
              </button>
              {change.change_type === 'Emergency' && (
                <span className="px-1.5 py-0.5 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/40">
                  Emergency
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(change.change_state)}`}>
                {change.change_state || 'Unknown'}
              </span>
              <span className={`flex items-center gap-1 text-xs ${getRiskColor(change.risk)}`}>
                {getRiskIcon(change.risk)}
                {change.risk || 'N/A'}
              </span>
            </div>
          </div>
          {isUpcoming && (
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              isUrgent ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
            }`}>
              {relativeTime}
            </div>
          )}
        </div>
      </div>

      {/* Middle Section */}
      <div className="p-4 space-y-3">
        {/* Description */}
        <p className={`text-sm text-text-primary ${expanded ? '' : 'line-clamp-2'}`}>
          {change.short_description || 'No description'}
        </p>

        {/* Assignment */}
        <div className="flex items-center gap-4 text-xs text-text-secondary">
          <span className="flex items-center gap-1">
            <User size={12} />
            {change.assigned_to || 'Unassigned'}
          </span>
          <span className="flex items-center gap-1">
            <Users size={12} />
            {change.assignment_group || 'N/A'}
          </span>
        </div>

        {/* Dates */}
        <div className="flex items-center gap-2 text-xs">
          <Calendar size={12} className="text-text-muted" />
          <span className="text-text-secondary">{formatDate(change.start_date)}</span>
          {duration && (
            <span className="px-1.5 py-0.5 rounded bg-dark-border/60 text-text-muted">
              {duration}
            </span>
          )}
        </div>

        {/* Category */}
        {change.category && (
          <div className="flex items-center gap-1">
            <Tag size={12} className="text-text-muted" />
            <span className="text-xs text-text-secondary">{change.category}</span>
          </div>
        )}
      </div>

      {/* Bottom Row */}
      <div className="px-4 py-3 border-t border-border/50 flex items-center justify-between">
        {/* CI Match Status */}
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1 text-xs ${ciMatch.color}`}>
            <Database size={12} />
            <span>{ciMatch.text}</span>
          </div>
          {ciMatch.needsReview > 0 && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/40">
              {ciMatch.needsReview} review
            </span>
          )}
        </div>

        {/* Downtime */}
        {change.downtime && change.downtime !== 'No' && (
          <span className="text-xs text-orange-400 flex items-center gap-1">
            <AlertTriangle size={12} />
            Downtime
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onViewDetails}
            className="p-1.5 rounded bg-dark-border/40 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 transition-all"
            title="View Details"
          >
            <Eye size={14} />
          </button>
          <button
            onClick={onToggleExpand}
            className="p-1.5 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-text-primary transition-all"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-border/30 space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-text-muted">Approval:</span>
              <span className="ml-2 text-text-primary">{change.approval_state || 'N/A'}</span>
            </div>
            <div>
              <span className="text-text-muted">Impact:</span>
              <span className="ml-2 text-text-primary">{change.impact || 'N/A'}</span>
            </div>
            <div>
              <span className="text-text-muted">Priority:</span>
              <span className="ml-2 text-text-primary">{change.priority || 'N/A'}</span>
            </div>
            <div>
              <span className="text-text-muted">Opened by:</span>
              <span className="ml-2 text-text-primary">{change.opened_by || 'N/A'}</span>
            </div>
          </div>
          {change.change_sys_id && (
            <a
              href={`https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${change.change_sys_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-400 hover:text-cyan-300"
            >
              <ExternalLink size={12} />
              Open in ServiceNow
            </a>
          )}
        </div>
      )}
    </div>
  );
};


// Table View Component
const TableView = ({ data, sortConfig, onSort, onViewDetails, getStatusColor, getRiskColor, formatDate, getCIMatchDisplay }) => {
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={12} className="opacity-50" />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border/50 bg-dark-border/20">
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400" onClick={() => onSort('change_number')}>
                <div className="flex items-center gap-1">Change # {getSortIcon('change_number')}</div>
              </th>
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Description</th>
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400" onClick={() => onSort('change_state')}>
                <div className="flex items-center gap-1">State {getSortIcon('change_state')}</div>
              </th>
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400" onClick={() => onSort('risk')}>
                <div className="flex items-center gap-1">Risk {getSortIcon('risk')}</div>
              </th>
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Assigned To</th>
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400" onClick={() => onSort('start_date')}>
                <div className="flex items-center gap-1">Start Date {getSortIcon('start_date')}</div>
              </th>
              <th className="text-left p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">CIs</th>
              <th className="text-center p-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((change, idx) => {
              const ciMatch = getCIMatchDisplay(change);
              return (
                <tr 
                  key={change.change_number} 
                  className="border-b border-border/30 hover:bg-dark-border/20 transition-all"
                  style={{ background: idx % 2 === 0 ? 'rgba(20, 20, 20, 0.3)' : 'transparent' }}
                >
                  <td className="p-3">
                    <button
                      onClick={() => onViewDetails(change.change_number)}
                      className="text-sm font-semibold text-blue-400 hover:text-purple-300"
                    >
                      {change.change_number}
                    </button>
                    {change.change_type === 'Emergency' && (
                      <span className="ml-2 px-1 py-0.5 text-xs rounded bg-red-500/20 text-red-400">E</span>
                    )}
                  </td>
                  <td className="p-3">
                    <p className="text-sm text-text-primary max-w-xs truncate" title={change.short_description}>
                      {change.short_description || 'No description'}
                    </p>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(change.change_state)}`}>
                      {change.change_state || 'Unknown'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`text-sm ${getRiskColor(change.risk)}`}>
                      {change.risk || 'N/A'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="text-sm text-text-primary">{change.assigned_to || 'Unassigned'}</div>
                    <div className="text-xs text-text-muted">{change.assignment_group || ''}</div>
                  </td>
                  <td className="p-3">
                    <div className="text-sm text-text-primary">{formatDate(change.start_date)}</div>
                  </td>
                  <td className="p-3">
                    <div className={`text-sm ${ciMatch.color}`}>{ciMatch.text}</div>
                    {ciMatch.needsReview > 0 && (
                      <span className="text-xs text-yellow-400">{ciMatch.needsReview} need review</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => onViewDetails(change.change_number)}
                        className="p-1.5 rounded bg-dark-border/40 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      {change.change_sys_id && (
                        <a
                          href={`https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${change.change_sys_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded bg-dark-border/40 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                          title="Open in ServiceNow"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, itemsPerPage, totalItems, onPageChange, onItemsPerPageChange }) => {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <span>Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="px-2 py-1 bg-dark-border/40 border border-border/60 rounded text-text-primary focus:outline-none focus:border-blue-500/60"
        >
          <option value={6}>6</option>
          <option value={12}>12</option>
          <option value={24}>24</option>
          <option value={48}>48</option>
        </select>
        <span>of {totalItems} changes</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeft size={16} />
        </button>
        
        <div className="flex items-center gap-1">
          {[...Array(Math.min(5, totalPages))].map((_, i) => {
            let page;
            if (totalPages <= 5) {
              page = i + 1;
            } else if (currentPage <= 3) {
              page = i + 1;
            } else if (currentPage >= totalPages - 2) {
              page = totalPages - 4 + i;
            } else {
              page = currentPage - 2 + i;
            }
            
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 rounded text-sm font-semibold transition-all ${
                  currentPage === page
                    ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                    : 'bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40'
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronRight size={16} />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="p-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Infrastructure;
